import { useCallback, useEffect, useMemo, useState } from 'react';
import { LambdaHistoryItem, lambdaOperationsApi } from '@api/lambdaOperationsApi';
import { ApiError } from '@api/RestClient';
import { isErrorResponse } from '@utils/utils';
import { isLambdaTaskRunning } from '../lambdaOperations.utils';

const POLLING_INTERVAL_MS = 10000;

export const useLambdaHistory = (presetId?: string) => {
    const [history, setHistory] = useState<LambdaHistoryItem[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<ApiError | null>(null);

    const hasRunningItems = useMemo(
        () => history.some(item => isLambdaTaskRunning(item.state)),
        [history]
    );

    const loadHistory = useCallback(
        async (abortSignal: AbortSignal, silent = false) => {
            if (!presetId) {
                setHistory([]);
                return;
            }

            if (!silent) {
                setLoading(true);
            }

            const resp = await lambdaOperationsApi.getCalculationHistory({
                presetId,
                abortSignal,
            });

            if (isErrorResponse(resp)) {
                setError(resp);
                if (!silent) {
                    setHistory([]);
                }
            } else {
                setError(null);
                setHistory(Array.isArray(resp) ? resp : []);
            }

            if (!silent) {
                setLoading(false);
            }
        },
        [presetId]
    );

    const refresh = useCallback(() => {
        const abortController = new AbortController();
        void loadHistory(abortController.signal);
    }, [loadHistory]);

    useEffect(() => {
        const abortController = new AbortController();
        void loadHistory(abortController.signal);

        return () => abortController.abort();
    }, [loadHistory]);

    useEffect(() => {
        if (!hasRunningItems || !presetId) {
            return;
        }

        const intervalId = window.setInterval(() => {
            const abortController = new AbortController();
            void loadHistory(abortController.signal, true);
        }, POLLING_INTERVAL_MS);

        return () => window.clearInterval(intervalId);
    }, [hasRunningItems, loadHistory, presetId]);

    return { history, loading, error, refresh, hasRunningItems };
};
